import { Fragment } from "react";
import { CheckIcon } from "@radix-ui/react-icons";
import { cn } from "@/lib/utils";
import { formatNumber } from "@/utils/format-fr";
import { BarList, type BarListRow } from "./ds";
import type { FormStepGauge, FormStepSummary, FormStepType } from "@/types/dynamic-detail";

type FormStepperProps = {
    steps: FormStepType[];
    /** Index of the step on screen. */
    current: number;
    /** Indexes of the steps already reached (they can be gone back to). */
    reached: number[];
    onGoTo: (index: number) => void;
};

/** Step indicator of a form filled in several steps (`FormConfig.steps`). */
export function FormStepper({ steps, current, reached, onGoTo }: FormStepperProps) {
    return (
        <ol className="flex flex-wrap items-center gap-2 text-sm" aria-label="Steps">
            {steps.map((step, index) => {
                const done = index < current;
                const reachable = done && reached.includes(index);
                return (
                    <li key={step.key} className="flex items-center gap-2">
                        {index > 0 && <span aria-hidden className="h-px w-6 bg-border" />}
                        <button
                            type="button"
                            disabled={!reachable}
                            onClick={() => onGoTo(index)}
                            aria-current={index === current ? "step" : undefined}
                            className={cn(
                                "flex items-center gap-2 rounded-md px-1 py-0.5 disabled:cursor-default",
                                index === current ? "font-medium text-black" : done ? "text-gray-700 hover:underline" : "text-gray-400"
                            )}
                        >
                            <span
                                className={cn(
                                    "flex h-6 w-6 items-center justify-center rounded-full border text-xs",
                                    index === current ? "border-primary bg-primary text-primary-foreground" : done ? "border-primary text-primary" : "border-border"
                                )}
                            >
                                {done ? <CheckIcon className="h-3.5 w-3.5" /> : index + 1}
                            </span>
                            {step.label}
                        </button>
                    </li>
                );
            })}
        </ol>
    );
}

type FormSummaryPanelProps = {
    summary: FormStepSummary | null;
    /** Labels of the steps still to fill. */
    nextSteps: string[];
    /** Gauges served by `form-step` (empty: none drawn), listed under their `group`. */
    gauges?: FormStepGauge[];
};

/** Gauges of one group: single-colour bars (`BarList`), the state in a badge, numbers in French format. */
export function FormGauges({ gauges }: { gauges: FormStepGauge[] }) {
    const rows: BarListRow[] = gauges.map(gauge => ({
        key: gauge.key,
        name: <span className={gauge.emphasis ? "font-medium text-black" : "text-gray-700"}>{gauge.label}</span>,
        ariaLabel: gauge.label,
        count: gauge.value,
        percentage: gauge.max > 0 ? (Math.max(gauge.value, 0) / gauge.max) * 100 : 0,
        badge: gauge.status ?? undefined,
        hint: gauge.lines?.length ? (
            <>
                {gauge.lines.map(line => (
                    <span key={line} className="block">
                        {line}
                    </span>
                ))}
            </>
        ) : undefined
    }));
    const byKey = Object.fromEntries(gauges.map(gauge => [gauge.key, gauge]));
    const valueLabel = (row: BarListRow) => {
        const gauge = byKey[row.key ?? ""];
        return gauge ? `${formatNumber(gauge.value, 2)} / ${formatNumber(gauge.max, 2)} ${gauge.unit}` : formatNumber(row.count, 2);
    };
    return <BarList rows={rows} scale="total" valueLabel={valueLabel} meter />;
}

/**
 * « Résumé »: what is chosen so far (served by `form-step`), and what comes next. An inset beside the
 * fields of the dialog; label above value rather than `FieldGrid`, whose label column is too narrow
 * at 320px.
 */
export function FormSummaryPanel({ summary, nextSteps, gauges = [] }: FormSummaryPanelProps) {
    const upcoming = [...nextSteps, ...(summary?.upcoming ?? [])];
    const groups: [string, FormStepGauge[]][] = [];
    gauges.forEach(gauge => {
        const group = groups.find(([title]) => title === gauge.group);
        if (group) group[1].push(gauge);
        else groups.push([gauge.group, [gauge]]);
    });
    return (
        <aside aria-label="Summary" className="space-y-4 rounded-sm border border-border bg-gray-50 p-3 text-sm">
            <h3 className="font-medium text-black">Summary</h3>
            {(summary?.sections ?? []).length === 0 && <p className="text-gray-500">Nothing selected yet.</p>}
            {(summary?.sections ?? []).map(section => (
                <section key={section.title} className="space-y-1">
                    <h4 className="text-xs font-medium text-gray-500">{section.title}</h4>
                    <dl className="space-y-1">
                        {section.items.map((item, index) => (
                            <div key={`${item.label}-${index}`} className="border-b border-border pb-1">
                                <dt className="text-xs text-gray-600">{item.label}</dt>
                                <dd className="min-w-0 break-words text-black">{item.value}</dd>
                            </div>
                        ))}
                    </dl>
                </section>
            ))}
            {groups.map(([group, items]) => (
                <section key={group} className="space-y-1" aria-label={group}>
                    <h4 className="text-xs font-medium text-gray-500">{group}</h4>
                    <FormGauges gauges={items} />
                </section>
            ))}
            {upcoming.length > 0 && (
                <section className="space-y-1">
                    <h4 className="text-xs font-medium text-gray-500">Next steps</h4>
                    <ul className="list-inside list-disc text-gray-600">
                        {upcoming.map(label => (
                            <li key={label}>{label}</li>
                        ))}
                    </ul>
                </section>
            )}
        </aside>
    );
}

type FormSummaryReviewProps = {
    summary: FormStepSummary | null;
    gauges?: FormStepGauge[];
    /** The step a section is edited at (its `step` key, else the step labelled as its title), once reached: « Edit » goes back to it. */
    editStep?: (section: { title: string; step?: string | null }) => (() => void) | null;
};

/**
 * The last step of a form in steps when it has no field of its own: the summary served by `form-step`,
 * laid out in full as a review (a card per section, label beside value), then what follows the creation.
 */
export function FormSummaryReview({ summary, gauges = [], editStep }: FormSummaryReviewProps) {
    const sections = summary?.sections ?? [];
    const upcoming = summary?.upcoming ?? [];
    const groups: [string, FormStepGauge[]][] = [];
    gauges.forEach(gauge => {
        const group = groups.find(([title]) => title === gauge.group);
        if (group) group[1].push(gauge);
        else groups.push([gauge.group, [gauge]]);
    });
    return (
        <div aria-label="Review" className="space-y-4 py-4 text-sm">
            <p className="text-gray-600">Check the record before creating it.</p>
            <div className="grid gap-3 md:grid-cols-2">
                {sections.map(section => {
                    const edit = editStep?.(section) ?? null;
                    return (
                        <section key={section.title} aria-label={section.title} className="rounded-md border border-border p-4">
                            <div className="flex items-center justify-between gap-2">
                                <h4 className="font-medium text-black">{section.title}</h4>
                                {edit && (
                                    <button type="button" onClick={edit} className="text-xs text-gray-500 underline-offset-2 hover:text-black hover:underline">
                                        Edit
                                    </button>
                                )}
                            </div>
                            <dl className="mt-3 grid grid-cols-[minmax(0,9rem)_minmax(0,1fr)] gap-x-4 gap-y-2">
                                {section.items.map((item, index) => (
                                    <Fragment key={`${item.label}-${index}`}>
                                        <dt className="text-gray-500">{item.label}</dt>
                                        <dd className="min-w-0 break-words text-black">{item.value}</dd>
                                    </Fragment>
                                ))}
                            </dl>
                        </section>
                    );
                })}
                {groups.map(([group, items]) => (
                    <section key={group} aria-label={group} className="rounded-md border border-border p-4">
                        <h4 className="mb-3 font-medium text-black">{group}</h4>
                        <FormGauges gauges={items} />
                    </section>
                ))}
            </div>
            {upcoming.length > 0 && (
                <section aria-label="After creation" className="rounded-md bg-gray-50 p-4">
                    <h4 className="font-medium text-black">After creation</h4>
                    <ol className="mt-2 list-decimal space-y-1 pl-5 text-gray-700">
                        {upcoming.map(label => (
                            <li key={label}>{label}</li>
                        ))}
                    </ol>
                </section>
            )}
        </div>
    );
}
